import { useState } from 'react'

import { signIn, useSession } from 'next-auth/react'

import { useForm } from 'react-hook-form'

import profileTextNodes from '@data/i18n/profile.json'

import { updatePassword } from '@lib/operations/customer'

import PasswordInput from '@components/form-controls/PasswordInput'

type UpdatePasswordForm = {
	oldPassword: string
	newPassword: string
}

export default function PasswordForm() {
	const session = useSession()

	const [error, setError] = useState<string | null>(null)
	const [message, setMessage] = useState<string | null>(null)

	// Update password form
	const {
		register: registerPassword,
		handleSubmit,
		watch: watchPassword,
		formState: { isSubmitting }
	} = useForm<UpdatePasswordForm>()

	// Watch password inputs - used to disable submit button
	const oldPasswordVal = watchPassword('oldPassword')
	const newPasswordVal = watchPassword('newPassword')

	// Disabled if:
	//  both password inputs dont have at least one character
	//  OR the form is being submitted
	const shouldDisableSubmit = !(!!oldPasswordVal && !!newPasswordVal) || isSubmitting

	const submitPassword = async ({ oldPassword, newPassword }: UpdatePasswordForm) => {
		// clean previous errors
		if (error) setError(null)
		if (message) setMessage(null)

		const userEmail = session.data?.user?.email
		const customerAccessToken = (session.data as any)?.shopifyAccessToken as string

		// TODO: move this to an API

		// check old password by trying to sign
		const signInResponse = await signIn<'credentials'>('credentials', {
			redirect: false,
			email: userEmail,
			password: oldPassword
		})

		if (!signInResponse?.ok) {
			setError('Old password is incorrect') // FIXME: better message?
			return
		}

		const updatePasswordOrError = await updatePassword(customerAccessToken, newPassword)

		if (updatePasswordOrError === true) {
			// reauthenticate user with the new password
			await signIn<'credentials'>('credentials', {
				redirect: false,
				email: userEmail,
				password: newPassword
			})

			setMessage('Password successfully updated')
		} else {
			for (const shopifyError of updatePasswordOrError) {
				setError(shopifyError.message)
			}
		}
	}

	return (
		<form
			className="flex flex-col gap-y-12 max-lg:border-t max-lg:border-gray-200 max-lg:pt-8"
			onSubmit={handleSubmit(async (data) => submitPassword(data))}
		>
			<fieldset className="flex flex-col space-y-4">
				<legend className="font-medium">
					{profileTextNodes.account.personalInformationForms.passwordTitle}
				</legend>

				{/* Old password */}
				<PasswordInput
					register={registerPassword}
					id="oldPassword"
					placeholder={profileTextNodes.account.personalInformationForms.oldPassword}
					canToggleVisible
				/>

				{/* New password */}
				<PasswordInput
					register={registerPassword}
					id="newPassword"
					placeholder={profileTextNodes.account.personalInformationForms.newPassword}
					canToggleVisible
				/>
			</fieldset>

			<button disabled={shouldDisableSubmit} className="button button-primary" type="submit">
				{isSubmitting
					? profileTextNodes.account.personalInformationForms.passwordCTALabelSubmitted
					: profileTextNodes.account.personalInformationForms.passwordCTALabel}
			</button>

			{/*  FIXME: @Drayke or @TJ, Make this nice :) */}
			{error && <span>{error}</span>}
			{message && <span>{message}</span>}
		</form>
	)
}
